import React, { useState, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlag } from '@fortawesome/free-solid-svg-icons';
import { EmptyStateMessage, Tile, ContentErrorBlock } from '../../detail/tabs/Tile';
import { CodeItem } from '../../detail/tabs/CodeItem';
import {
  CodeSuggestGroupHeader,
  PotentialPreExistingSectionHeader,
  isManualSearchGroup,
  getCodeSuggestCodeIds,
  buildExistingCodeById,
  resolveOverlap,
} from '../../detail/tabs/codeSuggest';
import { ConsultationContentType, EXPECTED_EXISTING_CODES_ERRORS, existingCodesEmptyMessage } from '../../detail/tabs/contentTypes';
import SearchBar from '../../detail/tabs/SearchBar';
import { ConsultationStatusEnum } from '../../enums/consultationStatusEnum';
import { ConsultationService, sharedApiClient } from '../../fetcher';
import { useNotifications } from '@medprompt/shared';
import { Significance } from '../../enums/significanceEnum';
import { getDurationDescription, getDurationOptions } from '../../enums/codeDurationEnum';

const consultationShouldShowContent = (field, status) => {
  return field !== undefined || status === ConsultationStatusEnum.READY.value;
};

const CodesTab = ({ 
  consultation, 
  isEditable, 
  isRegenerationEnabled, 
  handleRegenerationClick, 
  regenerateService,
  onConsultationUpdate,
  section,
  onTranscriptSearch,
}) => {
  const consultationManager = useMemo(() => new ConsultationService(sharedApiClient), []);
  const { addNotification } = useNotifications();
  
  const status = consultation?.status;
  const consultationId = consultation?.consultationId;
  
  // State for description editing
  const [editingCodeId, setEditingCodeId] = useState(null);
  const [editableDescription, setEditableDescription] = useState('');
  
  // State for duration dropdowns
  const [durationDropdownOpen, setDurationDropdownOpen] = useState({});

  // Consultation uses 'existingCodes' (ExistingPatientCodes with activeCodes and significantPastCodes)
  const existingPatientCodes = consultation?.existingCodes;
  // PatientConsultationData uses 'newCodes' (List<CodeSuggest>, each with phrase and suggestions)
  const newCodeSuggests = consultation?.newCodes;

  const showExistingCodes = consultationShouldShowContent(existingPatientCodes, status);
  const showNewCodes = consultationShouldShowContent(newCodeSuggests, status);

  // Toggle function for duration dropdown
  const toggleDurationDropdown = useCallback((codeId) => {
    setDurationDropdownOpen((prev) => ({
      ...prev,
      [codeId]: !prev[codeId],
    }));
  }, []);

  const clearEditingStateForCode = useCallback((codeId) => {
    if (editingCodeId === codeId) {
      setEditingCodeId(null);
      setEditableDescription('');
    }
    setDurationDropdownOpen((prev) => {
      if (!prev[codeId]) {
        return prev;
      }
      const next = { ...prev };
      delete next[codeId];
      return next;
    });
  }, [editingCodeId]);

  const handleDeleteExistingCode = useCallback(async (codeId) => {
    if (!isEditable || !consultationId || !codeId) return;

    const removeCode = (codes) => codes?.filter((codeWrapper) => codeWrapper.codeId !== codeId);

    const updatedActiveCodes = removeCode(existingPatientCodes?.activeCodes);
    const updatedPastCodes = removeCode(existingPatientCodes?.significantPastCodes);

    const updatedConsultation = {
      ...consultation,
      existingCodes: {
        ...existingPatientCodes,
        activeCodes: updatedActiveCodes,
        significantPastCodes: updatedPastCodes,
      },
    };

    if (onConsultationUpdate) {
      onConsultationUpdate(updatedConsultation);
    }

    clearEditingStateForCode(codeId);

    try {
      await consultationManager.deleteExistingCodes(consultationId, [codeId]);
    } catch (error) {
      console.error('Error deleting existing code:', error);
      addNotification(`code-delete-error-${codeId}`, 'Failed to remove code', { isError: true });
    }
  }, [isEditable, consultationId, existingPatientCodes, consultation, onConsultationUpdate, consultationManager, addNotification, clearEditingStateForCode]);

  const handleDeleteNewCodes = useCallback(async (codeIds) => {
    if (!isEditable || !consultationId || !codeIds?.length) return;

    const codeIdSet = new Set(codeIds);
    const updatedNewCodeSuggests = newCodeSuggests
      ?.map((codeSuggest) => ({
        ...codeSuggest,
        suggestions: codeSuggest.suggestions?.filter((suggestion) => !codeIdSet.has(suggestion.codeId)),
      }))
      .filter((codeSuggest) => codeSuggest.suggestions?.length > 0);

    const updatedConsultation = {
      ...consultation,
      newCodes: updatedNewCodeSuggests,
    };

    if (onConsultationUpdate) {
      onConsultationUpdate(updatedConsultation);
    }

    codeIds.forEach(clearEditingStateForCode);

    try {
      await consultationManager.deleteNewCodes(consultationId, codeIds);
    } catch (error) {
      console.error('Error deleting new code(s):', error);
      addNotification(`code-delete-error-${consultationId}`, 'Failed to remove code(s)', { isError: true });
    }
  }, [isEditable, consultationId, newCodeSuggests, consultation, onConsultationUpdate, consultationManager, addNotification, clearEditingStateForCode]);

  const handleDeleteNewCode = useCallback((codeId) => {
    handleDeleteNewCodes([codeId]);
  }, [handleDeleteNewCodes]);

  // Description editing handlers
  const handleDescriptionEditToggle = useCallback((codeId, currentDescription) => {
    if (editingCodeId === codeId) {
      setEditingCodeId(null);
      setEditableDescription('');
    } else {
      setEditingCodeId(codeId);
      setEditableDescription(currentDescription || '');
    }
  }, [editingCodeId]);

  const handleExistingDescriptionSave = useCallback(async (codeId, isNewCode = false, originalDescription = '') => {
    if (!isEditable || !consultationId) return;
    
    const hasChanges = editableDescription !== (originalDescription || '');
    if (!hasChanges) {
      setEditingCodeId(null);
      setEditableDescription('');
      return;
    }
    
    try {
      // Update existing code description
      const updateExistingCodes = (codes) => {
        return codes?.map(codeWrapper => 
          codeWrapper.codeId === codeId ? { ...codeWrapper, description: editableDescription } : codeWrapper
        );
      };

      const updatedActiveCodes = updateExistingCodes(existingPatientCodes?.activeCodes);
      const updatedPastCodes = updateExistingCodes(existingPatientCodes?.significantPastCodes);
      
      const updatedConsultation = { 
        ...consultation, 
        existingCodes: {
          ...existingPatientCodes,
          activeCodes: updatedActiveCodes,
          significantPastCodes: updatedPastCodes
        }
      };
      
      if (onConsultationUpdate) {
        onConsultationUpdate(updatedConsultation);
      }
      
      await consultationManager.updateConsultationExistingCode(consultationId, { codeId, description: editableDescription });
      
      setEditingCodeId(null);
      setEditableDescription('');
    } catch (error) {
      console.error('Error updating code description:', error);
      addNotification(`code-description-error-${codeId}`, 'Failed to update code description', { isError: true });
    }
  }, [isEditable, consultationId, editableDescription, existingPatientCodes, consultation, onConsultationUpdate, consultationManager, addNotification]);

  const handleNewDescriptionSave = useCallback(async (codeId, isNewCode = true, originalDescription = '') => {
    if (!isEditable || !consultationId) return;
    
    const hasChanges = editableDescription !== (originalDescription || '');
    if (!hasChanges) {
      setEditingCodeId(null);
      setEditableDescription('');
      return;
    }
    
    try {
      // Update new code description
      const updatedNewCodeSuggests = newCodeSuggests?.map(codeSuggest => ({
        ...codeSuggest,
        suggestions: codeSuggest.suggestions?.map(suggestion => 
          suggestion.codeId === codeId ? { ...suggestion, description: editableDescription } : suggestion
        )
      }));
      
      const updatedConsultation = { 
        ...consultation, 
        newCodes: updatedNewCodeSuggests
      };
      
      if (onConsultationUpdate) {
        onConsultationUpdate(updatedConsultation);
      }
      
      await consultationManager.updateConsultationNewCode(consultationId, { codeId, description: editableDescription });
      
      setEditingCodeId(null);
      setEditableDescription('');
    } catch (error) {
      console.error('Error updating new code description:', error);
      addNotification(`code-description-error-${codeId}`, 'Failed to update code description', { isError: true });
    }
  }, [isEditable, consultationId, editableDescription, newCodeSuggests, consultation, onConsultationUpdate, consultationManager, addNotification]);

  // Handler for existing code selection change
  const handleExistingCodeSelectionChange = useCallback((codeId, isChecked) => {
    if (!isEditable || !consultationId) return;
    
    // Update local state
    const updateExistingCodes = (codes) => {
      return codes?.map(codeWrapper => 
        codeWrapper.codeId === codeId ? { ...codeWrapper, isSelected: isChecked } : codeWrapper
      );
    };

    const updatedActiveCodes = updateExistingCodes(existingPatientCodes?.activeCodes);
    const updatedPastCodes = updateExistingCodes(existingPatientCodes?.significantPastCodes);
    
    const updatedConsultation = { 
      ...consultation, 
      existingCodes: {
        ...existingPatientCodes,
        activeCodes: updatedActiveCodes,
        significantPastCodes: updatedPastCodes
      }
    };
    
    if (onConsultationUpdate) {
      onConsultationUpdate(updatedConsultation);
    }
    
    const isSelected = isChecked ? true : null;
    const isDeselected = !isChecked ? true : null;
    consultationManager.updateConsultationExistingCode(consultationId, { codeId, isSelected, isDeselected })
      .catch(error => {
        console.error('Error updating existing code selection:', error);
        addNotification(`code-selection-error-${codeId}`, 'Failed to update code selection', { isError: true });
      });
  }, [isEditable, consultationId, existingPatientCodes, consultation, onConsultationUpdate, consultationManager, addNotification]);

  // Handler for new code selection change
  const handleNewCodeSelectionChange = useCallback((codeId, isChecked) => {
    if (!isEditable || !consultationId) return;
    
    const updatedNewCodeSuggests = newCodeSuggests?.map(codeSuggest => ({
      ...codeSuggest,
      suggestions: codeSuggest.suggestions?.map(suggestion => 
        suggestion.codeId === codeId ? { ...suggestion, isSelected: isChecked } : suggestion
      )
    }));
    
    const updatedConsultation = { 
      ...consultation, 
      newCodes: updatedNewCodeSuggests
    };
    
    if (onConsultationUpdate) {
      onConsultationUpdate(updatedConsultation);
    }
    
    const isSelected = isChecked ? true : null;
    const isDeselected = !isChecked ? true : null;
    consultationManager.updateConsultationNewCode(consultationId, { codeId, isSelected, isDeselected })
      .catch(error => {
        console.error('Error updating new code selection:', error);
        addNotification(`code-selection-error-${codeId}`, 'Failed to update code selection', { isError: true });
      });
  }, [isEditable, consultationId, newCodeSuggests, consultation, onConsultationUpdate, consultationManager, addNotification]);

  // Handler for existing code significance change
  const handleExistingCodeSignificanceChange = useCallback((codeId, newSignificance) => {
    if (!isEditable || !consultationId) return;
    
    const updateExistingCodes = (codes) => {
      return codes?.map(codeWrapper => 
        codeWrapper.codeId === codeId ? { ...codeWrapper, significance: newSignificance } : codeWrapper
      );
    };

    const updatedActiveCodes = updateExistingCodes(existingPatientCodes?.activeCodes);
    const updatedPastCodes = updateExistingCodes(existingPatientCodes?.significantPastCodes);
    
    const updatedConsultation = { 
      ...consultation, 
      existingCodes: {
        ...existingPatientCodes,
        activeCodes: updatedActiveCodes,
        significantPastCodes: updatedPastCodes
      }
    };
    
    if (onConsultationUpdate) {
      onConsultationUpdate(updatedConsultation);
    }
    
    consultationManager.updateConsultationExistingCode(consultationId, { codeId, significance: newSignificance })
      .catch(error => {
        console.error('Error updating existing code significance:', error);
        addNotification(`code-significance-error-${codeId}`, 'Failed to update code significance', { isError: true });
      });
  }, [isEditable, consultationId, existingPatientCodes, consultation, onConsultationUpdate, consultationManager, addNotification]);

  // Handler for new code significance change
  const handleNewCodeSignificanceChange = useCallback((codeId, newSignificance) => {
    if (!isEditable || !consultationId) return;
    
    const updatedNewCodeSuggests = newCodeSuggests?.map(codeSuggest => ({
      ...codeSuggest,
      suggestions: codeSuggest.suggestions?.map(suggestion => 
        suggestion.codeId === codeId ? { ...suggestion, significance: newSignificance } : suggestion
      )
    }));
    
    const updatedConsultation = { 
      ...consultation, 
      newCodes: updatedNewCodeSuggests
    };
    
    if (onConsultationUpdate) {
      onConsultationUpdate(updatedConsultation);
    }
    
    consultationManager.updateConsultationNewCode(consultationId, { codeId, significance: newSignificance })
      .catch(error => {
        console.error('Error updating new code significance:', error);
        addNotification(`code-significance-error-${codeId}`, 'Failed to update code significance', { isError: true });
      });
  }, [isEditable, consultationId, newCodeSuggests, consultation, onConsultationUpdate, consultationManager, addNotification]);

  // Handler for existing code duration change
  const handleExistingCodeDurationChange = useCallback((codeId, newDuration) => {
    if (!isEditable || !consultationId) return;
    
    const updateExistingCodes = (codes) => {
      return codes?.map(codeWrapper => 
        codeWrapper.codeId === codeId ? { ...codeWrapper, duration: newDuration } : codeWrapper
      );
    };

    const updatedActiveCodes = updateExistingCodes(existingPatientCodes?.activeCodes);
    const updatedPastCodes = updateExistingCodes(existingPatientCodes?.significantPastCodes);
    
    const updatedConsultation = { 
      ...consultation, 
      existingCodes: {
        ...existingPatientCodes,
        activeCodes: updatedActiveCodes,
        significantPastCodes: updatedPastCodes
      }
    };
    
    if (onConsultationUpdate) {
      onConsultationUpdate(updatedConsultation);
    }
    
    consultationManager.updateConsultationExistingCode(consultationId, { codeId, duration: newDuration, didChangeDuration: true })
      .catch(error => {
        console.error('Error updating existing code duration:', error);
        addNotification(`code-duration-error-${codeId}`, 'Failed to update code duration', { isError: true });
      });
    
    setDurationDropdownOpen((prev) => ({ ...prev, [codeId]: false }));
  }, [isEditable, consultationId, existingPatientCodes, consultation, onConsultationUpdate, consultationManager, addNotification]);

  // Handler for new code duration change
  const handleNewCodeDurationChange = useCallback((codeId, newDuration) => {
    if (!isEditable || !consultationId) return;
    
    const updatedNewCodeSuggests = newCodeSuggests?.map(codeSuggest => ({
      ...codeSuggest,
      suggestions: codeSuggest.suggestions?.map(suggestion => 
        suggestion.codeId === codeId ? { ...suggestion, duration: newDuration } : suggestion
      )
    }));
    
    const updatedConsultation = { 
      ...consultation, 
      newCodes: updatedNewCodeSuggests
    };
    
    if (onConsultationUpdate) {
      onConsultationUpdate(updatedConsultation);
    }
    
    consultationManager.updateConsultationNewCode(consultationId, { codeId, duration: newDuration, didChangeDuration: true })
      .catch(error => {
        console.error('Error updating new code duration:', error);
        addNotification(`code-duration-error-${codeId}`, 'Failed to update code duration', { isError: true });
      });
    
    setDurationDropdownOpen((prev) => ({ ...prev, [codeId]: false }));
  }, [isEditable, consultationId, newCodeSuggests, consultation, onConsultationUpdate, consultationManager, addNotification]);

  // Search handler for existing codes (patient's existing codes)
  const handleExistingCodesSearch = useCallback(async (query, callback) => {
    try {
      const results = await consultationManager.searchConsultationCodes(consultationId, query);
      callback(results);
    } catch (error) {
      console.error('Error searching consultation codes:', error);
      callback([]);
    }
  }, [consultationId, consultationManager]);

  // Handler for adding an existing code from search
  const handleAddExistingCode = useCallback(async (existingCode, category) => {
    if (!isEditable || !consultationId) return;
    try {
      const codeToAdd = {
        activeCodes: [],
        significantPastCodes: []
      };

      // Add the ExistingCode to the appropriate category
      if (category === 'Active') {
        codeToAdd.activeCodes.push(existingCode);
      } else {
        codeToAdd.significantPastCodes.push(existingCode);
      }

      await consultationManager.addExistingPatientCodes(consultationId, codeToAdd);
      addNotification(`existing-code-added-${consultationId}`, 'Existing code added successfully');
    } catch (error) {
      console.error('Error adding existing patient code:', error);
      addNotification(`existing-code-add-error-${consultationId}`, 'Failed to add code', { isError: true });
    }
  }, [isEditable, consultationId, consultationManager, addNotification]);

  // Search handler for new codes
  const handleNewCodesSearch = useCallback(async (query, callback) => {
    try {
      const results = await consultationManager.searchAllCodes(query);
      callback(results);
    } catch (error) {
      console.error('Error searching all codes:', error);
      callback([]);
    }
  }, [consultationManager]);

  // Handler for adding a new code from search
  const handleAddNewCode = useCallback(async (code) => {
    if (!isEditable || !consultationId) return;
    try {
      await consultationManager.addNewPatientCodes(consultationId, [code]);
      addNotification(`code-added-${consultationId}`, 'Code added successfully');
    } catch (error) {
      console.error('Error adding new patient code:', error);
      addNotification(`code-add-error-${consultationId}`, 'Failed to add code', { isError: true });
    }
  }, [isEditable, consultationId, consultationManager, addNotification]);

  // Render QoF badge
  const renderQofBadge = () => (
    <span className="qof-badge-inline">
      <FontAwesomeIcon icon={faFlag} />
      <div className="qof-badge-tooltip">This code is part of QOF</div>
    </span>
  );

  const emptyCodesMessage = existingCodesEmptyMessage(consultation);

  // Render existing codes section (activeCodes + significantPastCodes)
  const renderExistingCodes = () => {
    if (!existingPatientCodes) {
      return <EmptyStateMessage message={emptyCodesMessage} />;
    }

    const activeCodes = existingPatientCodes.activeCodes || [];
    const significantPastCodes = existingPatientCodes.significantPastCodes || [];

    if (activeCodes.length === 0 && significantPastCodes.length === 0) {
      return <EmptyStateMessage message={emptyCodesMessage} />;
    }

    return (
      <>
        {activeCodes.length > 0 && (
          <div>
            <div className="suggestion-header">
              <h4>Active Codes</h4>
              {activeCodes.length > 0 && isEditable && (
                <input 
                  type="checkbox" 
                  className="code-checkbox" 
                  onChange={(e) => {
                    const checked = e.target.checked;
                    activeCodes.forEach((code) => {
                      if (code.codeId) {
                        handleExistingCodeSelectionChange(code.codeId, checked);
                      }
                    });
                  }}
                  disabled={!isEditable}
                />
              )}
            </div>
            <p style={{ fontSize: '0.9em', color: '#666', fontStyle: 'italic', marginTop: '0', marginBottom: '8px' }}>
              Selected active codes are grouped with the existing problem in the patient's record
            </p>
            <div className="document-codes-list">
              {activeCodes.map((existingCode, index) => {
                const codeData = {
                  codeId: existingCode.codeId,
                  code: existingCode.code,
                  significance: existingCode.significance || Significance.MINOR.value,
                  description: existingCode.description,
                  duration: existingCode.duration,
                  isSelected: existingCode.isSelected,
                  timestamp: existingCode.timestamp,
                  quote: existingCode.quote
                };

                const qofContent = existingCode.code?.is_qof ? renderQofBadge() : null;

                return (
                  <CodeItem
                    key={codeData.codeId || `active_${index}`}
                    code={codeData}
                    index={index}
                    idPrefix="active"
                    onCodeSelectionChange={handleExistingCodeSelectionChange}
                    onSignificanceChange={handleExistingCodeSignificanceChange}
                    onDurationChange={handleExistingCodeDurationChange}
                    onDescriptionEdit={handleDescriptionEditToggle}
                    onDescriptionSave={handleExistingDescriptionSave}
                    editingCodeId={editingCodeId}
                    editableDescription={editableDescription}
                    setEditableDescription={setEditableDescription}
                    canEdit={isEditable}
                    shouldEnableSignificanceToggle={false}
                    canEditDuration={false}
                    qofContent={qofContent}
                    onQuoteClick={onTranscriptSearch}
                    onDelete={isEditable ? handleDeleteExistingCode : null}
                  />
                );
              })}
            </div>
          </div>
        )}

        {significantPastCodes.length > 0 && (
          <div>
            <div className="suggestion-header">
              <h4>Significant Past Codes</h4>
              {significantPastCodes.length > 0 && isEditable && (
                <input 
                  type="checkbox" 
                  className="code-checkbox" 
                  onChange={(e) => {
                    const checked = e.target.checked;
                    significantPastCodes.forEach((code) => {
                      if (code.codeId) {
                        handleExistingCodeSelectionChange(code.codeId, checked);
                      }
                    });
                  }}
                  disabled={!isEditable}
                />
              )}
            </div>
            <p style={{ fontSize: '0.9em', color: '#666', fontStyle: 'italic', marginTop: '0', marginBottom: '8px' }}>
              Selected significant past codes are created as new entries of the existing problem in the patient's record
            </p>
            <div className="document-codes-list">
              {significantPastCodes.map((existingCode, index) => {
                const codeData = {
                  codeId: existingCode.codeId,
                  code: existingCode.code,
                  significance: existingCode.significance || Significance.MINOR.value,
                  description: existingCode.description,
                  duration: existingCode.duration,
                  isSelected: existingCode.isSelected,
                  timestamp: existingCode.timestamp,
                  quote: existingCode.quote
                };

                const qofContent = existingCode.code?.is_qof ? renderQofBadge() : null;

                return (
                  <CodeItem
                    key={codeData.codeId || `past_${index}`}
                    code={codeData}
                    index={index}
                    idPrefix="past"
                    onCodeSelectionChange={handleExistingCodeSelectionChange}
                    onSignificanceChange={handleExistingCodeSignificanceChange}
                    onDurationChange={handleExistingCodeDurationChange}
                    onDescriptionEdit={handleDescriptionEditToggle}
                    onDescriptionSave={handleExistingDescriptionSave}
                    editingCodeId={editingCodeId}
                    editableDescription={editableDescription}
                    setEditableDescription={setEditableDescription}
                    canEdit={isEditable}
                    shouldEnableSignificanceToggle={true}
                    canEditDuration={true}
                    durationDropdownOpen={durationDropdownOpen}
                    toggleDurationDropdown={toggleDurationDropdown}
                    getDurationDescription={getDurationDescription}
                    getDurationOptions={getDurationOptions}
                    qofContent={qofContent}
                    onQuoteClick={onTranscriptSearch}
                    onDelete={isEditable ? handleDeleteExistingCode : null}
                  />
                );
              })}
            </div>
          </div>
        )}
      </>
    );
  };

  // Render new codes section (CodeSuggest with phrase and suggestions)
  const renderNewCodes = () => {
    if (!newCodeSuggests || newCodeSuggests.length === 0) {
      return (
        <>
          <ContentErrorBlock
            doc={consultation}
            entityId={consultationId}
            contentType={ConsultationContentType.POTENTIAL_EXISTING_CODES_STATUS}
            isRetryEnabled={isRegenerationEnabled ? isRegenerationEnabled() : false}
            onRegenerateClick={handleRegenerationClick}
            regenerateService={regenerateService}
            shouldShowError={() => !(consultation?.potentialExistingCodes?.length > 0)}
            compact
          />
          <EmptyStateMessage message="No new codes suggested" />
        </>
      );
    }

    const codesById = buildExistingCodeById(existingPatientCodes);
    const overlapsByIndex = new Map(
      (consultation?.potentialExistingCodes || []).map((overlap) => [overlap.newSuggestIndex, overlap])
    );

    const manualSearchGroups = [];
    const unflaggedGroups = [];
    const flaggedGroups = [];

    newCodeSuggests.forEach((codeSuggest, originalIndex) => {
      if (isManualSearchGroup(codeSuggest)) {
        manualSearchGroups.push({ codeSuggest, originalIndex });
        return;
      }

      const overlap = resolveOverlap(overlapsByIndex.get(originalIndex), codesById);
      const group = { codeSuggest, originalIndex, overlap };
      if (overlap) {
        flaggedGroups.push(group);
      } else {
        unflaggedGroups.push(group);
      }
    });

    const topGroups = [...manualSearchGroups, ...unflaggedGroups];

    const renderSuggestGroup = (group, index) => {
      const { codeSuggest, overlap, originalIndex } = group;
      const suggestions = codeSuggest.suggestions || [];
      if (suggestions.length === 0) {
        return null;
      }

      const selectedSuggestion = suggestions.find((s) => s.isSelected);
      const hasSelection = !!selectedSuggestion;
      const isManualSearch = isManualSearchGroup(codeSuggest);

      return (
        <div
          key={`${codeSuggest.phrase}-${originalIndex ?? index}`}
          className={`new-code-group ${isManualSearch ? 'manual-search-group' : ''} ${overlap ? 'has-overlap' : ''}`}
        >
          <CodeSuggestGroupHeader
            codeSuggest={codeSuggest}
            canEdit={isEditable}
            onDeleteGroup={handleDeleteNewCodes}
            overlap={overlap}
            onExistingCodeSelect={isEditable ? handleExistingCodeSelectionChange : null}
          />
          <div className="document-codes-list">
            {suggestions.map((newCode, codeIndex) => {
              const isSelected = newCode.isSelected;
              const shouldCollapse = !isManualSearch && hasSelection && !isSelected;

              const codeData = {
                codeId: newCode.codeId,
                code: newCode.record,
                significance: newCode.significance || Significance.MAJOR.value,
                description: newCode.description,
                duration: newCode.duration,
                isSelected: newCode.isSelected,
                quote: isManualSearch ? newCode.quote : (newCode.quote || codeSuggest.phrase)
              };

              const qofContent = newCode.record?.is_qof ? renderQofBadge() : null;

              return (
                <div
                  key={codeData.codeId || `new_${originalIndex}_${codeIndex}`}
                  className={`code-item-wrapper ${shouldCollapse ? 'collapsed' : 'expanded'}`}
                >
                  <CodeItem
                    code={codeData}
                    index={codeIndex}
                    idPrefix={`new_${index}`}
                    onCodeSelectionChange={handleNewCodeSelectionChange}
                    onSignificanceChange={handleNewCodeSignificanceChange}
                    onDurationChange={handleNewCodeDurationChange}
                    onDescriptionEdit={handleDescriptionEditToggle}
                    onDescriptionSave={(codeId, isNew, origDesc) => handleNewDescriptionSave(codeId, true, origDesc)}
                    editingCodeId={editingCodeId}
                    editableDescription={editableDescription}
                    setEditableDescription={setEditableDescription}
                    canEdit={isEditable}
                    shouldEnableSignificanceToggle={true}
                    canEditDuration={true}
                    durationDropdownOpen={durationDropdownOpen}
                    toggleDurationDropdown={toggleDurationDropdown}
                    getDurationDescription={getDurationDescription}
                    getDurationOptions={getDurationOptions}
                    qofContent={qofContent}
                    onQuoteClick={isManualSearch ? null : onTranscriptSearch}
                    onDelete={isEditable ? handleDeleteNewCode : null}
                  />
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    return (
      <>
        {topGroups.map((group, index) => renderSuggestGroup(group, index))}
        <ContentErrorBlock
          doc={consultation}
          entityId={consultationId}
          contentType={ConsultationContentType.POTENTIAL_EXISTING_CODES_STATUS}
          isRetryEnabled={isRegenerationEnabled ? isRegenerationEnabled() : false}
          onRegenerateClick={handleRegenerationClick}
          regenerateService={regenerateService}
          shouldShowError={() => !(consultation?.potentialExistingCodes?.length > 0)}
          compact
        />
        {flaggedGroups.length > 0 && (
          <div className="new-code-overlap-section">
            <PotentialPreExistingSectionHeader
              canEdit={isEditable}
              onDelete={() => {
                const codeIds = flaggedGroups.flatMap(({ codeSuggest }) => getCodeSuggestCodeIds(codeSuggest));
                if (codeIds.length) {
                  handleDeleteNewCodes(codeIds);
                }
              }}
            />
            <div className="new-code-overlap-section-items">
              {flaggedGroups.map((group, index) => renderSuggestGroup(group, `overlap-${index}`))}
            </div>
          </div>
        )}
      </>
    );
  };

  const existingCodesTile = (
    <Tile
      doc={consultation}
      entityId={consultationId}
      contentType={ConsultationContentType.EXISTING_CODES_STATUS}
      title="Existing Codes"
      condition={showExistingCodes}
      isEnabled={isRegenerationEnabled ? isRegenerationEnabled() : false}
      onRegenerateClick={handleRegenerationClick}
      regenerateService={regenerateService}
      showContentOnError
      shouldShowError={(error) => {
        if (EXPECTED_EXISTING_CODES_ERRORS.has(error.message)) {
          return false;
        }
        const existing = consultation?.existingCodes;
        const count = (existing?.activeCodes?.length || 0) + (existing?.significantPastCodes?.length || 0);
        return count === 0;
      }}
      preContent={isEditable && consultation?.patientId ? (
        <SearchBar
          placeholder="Search patient's existing codes..."
          onSearch={handleExistingCodesSearch}
          onResultClick={(result) => handleAddExistingCode(result, result.category)}
        />
      ) : null}
    >
      {renderExistingCodes()}
    </Tile>
  );

  const newCodesTile = (
    <Tile
      doc={consultation}
      entityId={consultationId}
      contentType={ConsultationContentType.NEW_CODES_STATUS}
      title="New Codes"
      condition={showNewCodes}
      isEnabled={isRegenerationEnabled ? isRegenerationEnabled() : false}
      onRegenerateClick={handleRegenerationClick}
      regenerateService={regenerateService}
      showContentOnError
      shouldShowError={() => !(consultation?.newCodes?.length > 0)}
      preContent={
        <>
          <div className="tile-header-content">
            <p style={{ fontSize: '0.9em', color: '#666', fontStyle: 'italic' }}>
              New codes suggested from the consultation
            </p>
          </div>
          {isEditable && (
            <SearchBar
              placeholder="Search SNOMED database for codes..."
              onSearch={handleNewCodesSearch}
              onResultClick={(result) => handleAddNewCode(result)}
            />
          )}
        </>
      }
    >
      {renderNewCodes()}
    </Tile>
  );

  const showExisting = section === 'existing';
  const showNew = section === 'new';

  return (
    <div className="document-detail-tiles">
      {showExisting && existingCodesTile}
      {showNew && newCodesTile}
    </div>
  );
};

export default CodesTab;
